import React, { Component } from 'react';
import { isNil, isString, noop } from 'lodash';
import { Overlay } from 'src/common-ui';
import container from 'src/ServiceContainer';
import { ASSORTMENT } from 'src/utils/Domain/Constants';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';
import { AppType } from 'configuration-schemas/general';
import { MasterDetailChart } from 'src/components/MasterDetailRenderer/MasterDetailChart';
import { processApiParams } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.utils';
import ServiceContainer from 'src/ServiceContainer';
import { ClientDataApi, isListDataApi } from 'configuration-schemas/confdefnView';
import EditableGrid, { EditableGridProps } from '../ConfigurableGrid/EditableGrid/EditableGrid';
import Axios from 'src/services/axios';
import { MasterDetailInsights } from 'src/components/MasterDetailRenderer/MasterDetailInsights';
import {
  type MasterDetailChartConfig,
  type MasterDetailGridConfig,
  type MasterDetailInsightsConfig,
  MasterDetailRendererType,
  type MasterDetailRendererProps,
  type MasterDetailRendererState,
} from 'src/components/MasterDetailRenderer/MasterDetail.types';

export class MasterDetailRenderer extends Component<MasterDetailRendererProps, MasterDetailRendererState> {
  constructor(props: MasterDetailRendererProps) {
    super(props);

    this.state = {
      isLoading: true,
      config: null,
      data: [],
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  async fetchData() {
    const { configApi, dataApi, data, type } = this.props;
    const configParams = configApi.params;
    // convert to switch statement if more types are added
    // const viewDefnType =  typeof MasterDetailChartConfig : typeof MasterDetailInsightsConfig;

    const untypedConfig = await container.tenantConfigClient.getTenantViewDefn({
      defnId: !isNil(configParams) ? configParams.defnId : '',
      appName: !isNil(configParams) ? (configParams.appName as AppType) : ASSORTMENT,
    });
    let config = null;

    switch (type) {
      case MasterDetailRendererType.chart:
        config = untypedConfig as MasterDetailChartConfig;
        break;
      case MasterDetailRendererType.editableGrid:
        config = untypedConfig as MasterDetailGridConfig;
        break;
      case MasterDetailRendererType.insights:
        config = untypedConfig as MasterDetailInsightsConfig;
        break;
    }

    const processedApi = processApiParams(dataApi, data);

    let pivotData: BasicPivotItem[] = [];

    if (isListDataApi(processedApi)) {
      pivotData = await ServiceContainer.pivotService
        .listData(processedApi.defnId, AppType.Assortment, processedApi.params)
        .then((resp) => resp.flat);
    } else {
      const clientDataApi = dataApi as ClientDataApi;
      const { url: dataUrl, headers: dataHeaders } = clientDataApi;
      const dataOptions = {
        headers: dataHeaders,
        params: {
          ...processedApi.params,
        },
      };

      pivotData = await Axios.get(dataUrl, dataOptions)
        .then(async (resp) => {
          const dataResponse = resp.data;
          return isString(dataResponse) ? ServiceContainer.pivotService.deserialize(dataResponse) : dataResponse.list;
        })
        .then((formattedData: BasicPivotItem[]) => {
          // If we are dealing with a nested set, first level must be single item, gets second level
          // (/floorset returns [{channel: ..., children: [<row_records>]}])
          if (formattedData && formattedData[0] && formattedData[0].children && formattedData[0].children.length > 0) {
            return formattedData[0].children;
          } else {
            return formattedData;
          }
        });
    }

    this.setState({
      isLoading: false,
      config,
      data: pivotData,
    });
  }

  render() {
    const { isLoading, config, data } = this.state;

    if (isLoading || isNil(config)) {
      return <Overlay visible={true} type={'loading'} fitParent={true} />;
    }

    switch (config.renderer) {
      case MasterDetailRendererType.chart:
        return <MasterDetailChart config={config as MasterDetailChartConfig} data={data} />;
      case MasterDetailRendererType.insights:
        return <MasterDetailInsights config={config as MasterDetailInsightsConfig} data={data} />;
      case MasterDetailRendererType.editableGrid:
        const processedApi = processApiParams(this.props.dataApi, this.props.data);
        const anchorGridProps: EditableGridProps = {
          configLoaded: !isNil(config),
          dataLoaded: true,
          data: data,
          columnDefs: config.columns,
          updateCoordinateMap: config.updateCoordinateMap,
          massEditConfig: config.massEditConfig,
          idProp: config.idProp,
          leafIdProp: config.leafIdProp,
          salesAdjustmentConfig: config.salesAdjustmentConfig,
          clientActionHandlers: config.clientActionHandlers,
          onlySendCoordinateMap: config.onlySendCoordinateMap,
          handleGridReady: noop,
          dataApi: processedApi,
          addCrumb: this.props.addCrumb,
          routerNavigate: this.props.routerNavigate,
          // TODO: Not sure if we want to handle context around breadcrumbing
          onFirstDataRendered: noop,
        } as EditableGridProps;
        const margins: React.CSSProperties = {
          paddingTop: 15,
          paddingBottom: 15,
          paddingLeft: 30,
          paddingRight: 30,
        };
        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100%',
              ...margins,
              backgroundColor: 'rgb(249, 249, 249)',
            }}
          >
            <div style={{ width: '100%', height: '100%', border: '1px solid grey' }}>
              <EditableGrid {...anchorGridProps} />
            </div>
          </div>
        );
      default:
        return <span>Unknown custom renderer for master detail.</span>;
    }
  }
}
